var _a, _b, _c, _d, _e;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import view from './modelConfigurationView.html';
import { role } from '../../../../types/instructions';
import { IScope } from 'angular';
import { isValidHistoryLength, isValidTemperatureRange, isValidTokenRange } from '../../../../utils/AgentUtils';
import { BlipToastService } from 'modules/shared/BlipToastService';
import { AiAgentsFeatures } from 'modules/application/detail/aiAgents/AiAgentsFeatures';
import { hasPdfFileRestriction } from '../../../../utils/ModelUtils';
import { AgentContextService } from '../../../../services/AgentContextService';
let ModelConfiguration = class ModelConfiguration {
    static $inject = ['clipboard', '$scope', 'BlipToastService', '$translate', 'AgentContextService'];
    //#endregion
    //#region Constructor
    constructor(clipboard, $scope, BlipToastService, $translate, AgentContextService) {
        this.clipboard = clipboard;
        this.$scope = $scope;
        this.BlipToastService = BlipToastService;
        this.$translate = $translate;
        this.AgentContextService = AgentContextService;
        this.historyLength = 50;
        this.historyOrder = 'desc';
        this.historyEnabled = false;
        this.memoryEnabled = false;
        this.showLongTermMemory = false;
        this.agentContext = 'agent-idrw54556';
        this.invalidFields = {
            maxTokens: false,
            temperature: false,
            historyLength: false,
        };
        this.shouldShowPdfRestrictionBanner = false;
        this.initialModelHasPdfRestriction = false;
        //#endregion
        //#region Consts
        this.MAX_TOKENS = 500000;
        this.MIN_TOKENS = 1;
        this.MIN_TEMPERATURE = 0;
        this.MAX_TEMPERATURE = 2;
        this.MIN_HISTORY_LENGTH = 1;
        this.MAX_HISTORY_LENGTH = 100;
        //#endregion
        //#region Methods
        this.onTemperatureChange = (event) => {
            if (this.agentConfiguration?.model) {
                [this.agentConfiguration.model.temperature, this.invalidFields.temperature] = this.extractNumberCustomEvent(event, (value) => parseFloat(value), this.MIN_TEMPERATURE);
            }
        };
        this.onTokenChange = (event) => {
            if (this.agentConfiguration?.model) {
                [this.agentConfiguration.model.maxTokens, this.invalidFields.maxTokens] = this.extractNumberCustomEvent(event, (value) => parseInt(value, 10), this.MIN_TOKENS);
            }
        };
        this.onHistoryChange = (event) => {
            [this.historyLength, this.invalidFields.historyLength] = this.extractNumberCustomEvent(event, (value) => parseInt(value, 10), this.MIN_HISTORY_LENGTH);
            if (this.agentConfiguration?.prompt) {
                const shortTermMemory = this
                    .agentConfiguration.prompt
                    .find(item => item.type === role.shortTermMemory);
                if (shortTermMemory) {
                    shortTermMemory.config.length = this.historyLength;
                    shortTermMemory.config.order = this.historyOrder;
                }
                // Update the agent context service with the new historyLength
                this.AgentContextService.addOrUpdateContext(this.stateId, undefined, this.historyLength);
                this.$scope.$apply();
            }
        };
        this.onOrderChange = (event) => {
            this.historyOrder = event.detail.value;
            if (this.agentConfiguration?.prompt) {
                const shortTermMemory = this
                    .agentConfiguration.prompt
                    .find(item => item.type === role.shortTermMemory);
                if (shortTermMemory) {
                    shortTermMemory.config.order = this.historyOrder;
                }
                this.$scope.$apply();
            }
        };
        this.onModelChange = (event) => {
            if (this.agentConfiguration?.model) {
                this.agentConfiguration.model.model = event.detail.value;
                this.modelSelected = event.detail.value;
                this.evaluatePdfAccessBanner();
            }
        };
        this.onHistoryToggle = (event) => {
            if (this.historyEnabled === event.detail.checked) {
                // prevent redundant operations
                return;
            }
            this.historyEnabled = event.detail.checked;
            if (!this.historyEnabled) {
                this.agentConfiguration.prompt = this.agentConfiguration.prompt.filter(item => item.type !== role.shortTermMemory);
                return;
            }
            if (this.agentConfiguration.prompt.some(item => item.type === role.shortTermMemory)) {
                return;
            }
            const shortTermMemory = {
                type: role.shortTermMemory,
                config: {
                    length: this.historyLength,
                    order: this.historyOrder,
                }
            };
            this.agentConfiguration.prompt = [...this.agentConfiguration.prompt, shortTermMemory];
        };
        this.onMemoryToggle = (event) => {
            this.memoryEnabled = event.detail.checked;
            if (!this.memoryEnabled) {
                // Disables both memory options when the toggle is turned off
                if (this.agentConfiguration.longTermMemory) {
                    this.agentConfiguration.longTermMemory.queryMemory = false;
                    this.agentConfiguration.longTermMemory.changeMemory = false;
                }
            }
            else if (this.agentConfiguration.longTermMemory) {
                // Enables the first checkbox (Query memory) when the toggle is turned on
                this.agentConfiguration.longTermMemory.queryMemory = true;
            }
        };
        this.onQueryMemoryChange = (event) => {
            if (this.agentConfiguration.longTermMemory) {
                this.agentConfiguration.longTermMemory.queryMemory = event.detail.checked;
            }
        };
        this.onChangeMemoryChange = (event) => {
            if (this.agentConfiguration.longTermMemory) {
                this.agentConfiguration.longTermMemory.changeMemory = event.detail.checked;
            }
        };
        this.copyContextVariable = () => {
            this.clipboard.copyText(this.agentContext);
            this.BlipToastService.show('success', {
                msg: this.$translate.instant('modelConfiguration.history.copyVariableToast')
            });
        };
        //#endregion
        //#region Helpers
        /**
         * Extracts a number from a custom event, applying a parser and a default value.
         * @param event
         * @param parser
         * @param defaultValue
         * @returns [Parsed value, isInvalid]
         */
        this.extractNumberCustomEvent = (event, parser, defaultValue) => {
            const value = this.removeNonNumericCharacters(event.detail.value);
            if (typeof value === 'number') {
                return [value, false];
            }
            if (typeof value === 'string') {
                const parsedValue = parser(value);
                return isNaN(parsedValue) ? [defaultValue, true] : [parsedValue, false];
            }
            return [defaultValue, true];
        };
        this.isInvalidTokenRange = () => {
            return !isValidTokenRange(this.agentConfiguration.model.maxTokens);
        };
        this.isInvalidTemperatureRange = () => {
            return !isValidTemperatureRange(this.agentConfiguration.model.temperature);
        };
        this.isInvalidHistoryLength = () => {
            if (!this.historyEnabled) {
                return false;
            }
            return !isValidHistoryLength(this.historyLength);
        };
        this.removeNonNumericCharacters = (value) => {
            return value.replace(',', '.').replace(/[^0-9.]/g, '');
        };
        this.isQueryMemorySelected = () => {
            return this.agentConfiguration?.longTermMemory?.queryMemory ?? false;
        };
        this.isChangeMemorySelected = () => {
            return this.agentConfiguration?.longTermMemory?.changeMemory ?? false;
        };
        this.isAtLeastOneOutputSelected = () => {
            return this.isQueryMemorySelected() || this.isChangeMemorySelected();
        };
        this.evaluatePdfAccessBanner = () => {
            const isPdfFileRestricted = hasPdfFileRestriction(this.modelSelected);
            this.shouldShowPdfRestrictionBanner = !this.initialModelHasPdfRestriction && isPdfFileRestricted;
        };
    }
    //#endregion
    //#region Lifecycle Hooks
    async $onInit() {
        this.showLongTermMemory = await AiAgentsFeatures.isAiAgentLongTermMemoryEnabled();
        if (this.agentConfiguration?.model) {
            this.modelSelected = this.agentConfiguration.model.model;
            this.initialModelHasPdfRestriction = hasPdfFileRestriction(this.modelSelected);
            this.evaluatePdfAccessBanner();
        }
        if (this.agentConfiguration?.prompt) {
            const shortTermMemory = this
                .agentConfiguration.prompt
                .find(item => item.type === role.shortTermMemory);
            if (shortTermMemory) {
                this.historyLength = shortTermMemory.config.length;
                this.historyOrder = shortTermMemory.config.order || 'desc';
                this.historyEnabled = true;
            }
        }
        if (this.showLongTermMemory && this.agentConfiguration) {
            if (!this.agentConfiguration.longTermMemory) {
                this.agentConfiguration.longTermMemory = {
                    queryMemory: false,
                    changeMemory: false,
                };
            }
            this.memoryEnabled = this.agentConfiguration.longTermMemory.queryMemory || this.agentConfiguration.longTermMemory.changeMemory;
        }
        this.agentContext = 'shorttermmemory' + this.stateId.replace(/\-|\:/g, '');
        // Register this agent context in the global service with historyLength
        this.AgentContextService.addOrUpdateContext(this.stateId, undefined, this.historyLength);
        this.invalidFields = {
            maxTokens: !isValidTokenRange(this.agentConfiguration.model.maxTokens),
            temperature: !isValidTemperatureRange(this.agentConfiguration.model.temperature),
            historyLength: !isValidHistoryLength(this.historyLength),
        };
    }
};
ModelConfiguration = __decorate([
    Component({
        template: view,
        controllerAs: '$ctrl',
        bindings: {
            agentConfiguration: '<',
            availableModels: '<',
            stateId: '<',
        },
    }),
    __metadata("design:paramtypes", [Object, typeof (_a = typeof IScope !== "undefined" && IScope) === "function" ? _a : Object, typeof (_b = typeof BlipToastService !== "undefined" && BlipToastService) === "function" ? _b : Object, typeof (_d = typeof angular !== "undefined" && (_c = angular.translate) !== void 0 && _c.ITranslateService) === "function" ? _d : Object, typeof (_e = typeof AgentContextService !== "undefined" && AgentContextService) === "function" ? _e : Object])
], ModelConfiguration);
export { ModelConfiguration };
